// What the staff chat hub is doing (client-only state), for the dock's reconnecting strip, and when the other person
// last read each conversation, for "Seen" (staff chat MVP, SR-14).
//
// "Seen" (lead ruling, 1 Nov): the hub's StaffChatRead already carries { threadId, userId, readAt }. The thread DTO has no
// per-participant read time, so this only knows reads that arrive live: after a reload a message reads "Sent" until the
// other person reads again. No backend change for the MVP.
import { create } from 'zustand';

export type HubStatus = 'idle' | 'connected' | 'reconnecting';

interface HubStatusState {
  status: HubStatus;
  /** `${who}|${threadId}` to the latest readAt (ISO) by someone other than the caller. */
  peerReadAt: Record<string, string>;
  setStatus: (status: HubStatus) => void;
  notePeerRead: (who: string, threadId: string, readAt: string) => void;
}

export const peerReadKey = (who: string, threadId: string) => `${who}|${threadId}`;

export const useHubStatusStore = create<HubStatusState>((set) => ({
  status: 'idle',
  peerReadAt: {},
  setStatus: (status) => set({ status }),
  notePeerRead: (who, threadId, readAt) =>
    set((s) => {
      const key = peerReadKey(who, threadId);
      const prev = s.peerReadAt[key];
      if (prev && Date.parse(prev) >= Date.parse(readAt)) return s;
      return { peerReadAt: { ...s.peerReadAt, [key]: readAt } };
    }),
}));

/**
 * Resolves once the hub is not reconnecting. A send waits here while the socket is down, so the reconnecting strip's
 * "What you send waits and goes when the connection is back" is true: the message shows "Sending…" and goes when the
 * hub is back (connected), or when it gives up (idle: the send then goes and succeeds or fails on its own).
 */
export function whenHubNotReconnecting(): Promise<void> {
  if (useHubStatusStore.getState().status !== 'reconnecting') return Promise.resolve();
  return new Promise((resolve) => {
    const stop = useHubStatusStore.subscribe((st) => {
      if (st.status === 'reconnecting') return;
      stop();
      resolve();
    });
  });
}
